/**
 *
 *  BundlePriceBlock
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 13/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState } from "react";
import PropTypes from "prop-types"; // use directly

// Icons
import MapMarkerIcon from "../../icons/MapMarkerIcon";
import SessionTypeOnlineIcon from "../../icons/SessionTypeOnlineIcon";
import SessionsBundleOnlineIcon from "../../icons/SessionsBundleOnlineIcon";

// Utils
// import { getBundlesByPartnerId } from "../../utils";
import { uniqueId } from "../../utils/_";

export default function BundlePriceBox({
  bundles,
  customerBundles,
  sessionType,
  partnerId,
  partnerFirstName,
  price,
}) {
  const [iconId] = useState(uniqueId("tb_"));
  const isOnline = false;

  // const activeBundles = getBundlesByPartnerId(customerBundles, partnerId);

  const activeBundles = [
    {
      id: "1",
      status: "RUNNING",
      sessionsCount: 10,
      sessionsUsed: 10,
      type: "F2F",
    },
  ];

  let content,
    activeBundle,
    hasSessions,
    bundleLine = [];

  if (activeBundles && activeBundles.length) {
    activeBundle = activeBundles.find(
      (bundle) =>
        bundle.type === (isOnline ? "LS" : "F2F") && bundle.status !== "SPENT"
    );

    if (
      activeBundle &&
      (hasSessions = activeBundle.sessionsCount - activeBundle.sessionsUsed) > 0
    ) {
      for (let i = 0; i < activeBundle.sessionsCount; i++) {
        let className = `tru-bar tbk-relative tbk-w-full tbk-h-1 tbk-mr-[2px] ${
          i < activeBundle.sessionsCount - activeBundle.sessionsUsed
            ? isOnline
              ? "tbk-bg-blue"
              : "tbk-bg-coral"
            : "tbk-bg-grey-inactive"
        }${i === 0 ? " tbk-rounded-l-lg" : ""}${
          i === activeBundle.sessionsCount - 1 ? " tbk-rounded-r-lg" : ""
        }`;

        bundleLine.push(<div className={className} key={i} />);
      }
    }
  }

  if (activeBundle && hasSessions) {
    let sessionsLeft = activeBundle.sessionsCount - activeBundle.sessionsUsed;

    content = (
      <div className="bundle-pricing-box tbk--mt-3 tbk-flex tbk-flex-wrap tbk-justify-center tbk-text-center">
        <div className="tbk-relative tbk--bottom-3 tbk-inline-flex tbk-min-w-0 tbk-items-center tbk-self-center tbk-rounded-lg tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-bg-basic-white tbk-py-0.5 tbk-px-1.5 tbk-text-primary">
          {isOnline ? (
            <SessionTypeOnlineIcon
              id={iconId}
              className="tbk-mr-0.5 tbk-shrink-0"
            />
          ) : (
            <MapMarkerIcon className="tbk-shrink-0 tbk-text-coral" size={32} />
          )}

          <div className="bundle-pricing-box-title tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
            1-hour {isOnline ? "Online" : "In-Person"} Session with{" "}
            {partnerFirstName}
          </div>
        </div>
        <div className="tbk-min-w-full tbk-rounded-lg tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-3">
          <div className="tbk-mt-6 tbk-mb-4 tbk-flex tbk-items-center tbk-justify-center tbk-pt-2">
            {isOnline ? (
              <SessionsBundleOnlineIcon id={iconId} className="tbk-mr-2" />
            ) : (
              <MapMarkerIcon
                className="tbk-mr-2 tbk-shrink-0 tbk-text-coral"
                size={32}
              />
            )}
            <div
              className={`tbk-text-main ${
                hasSessions ? "tbk-text-primary" : "tbk-text-grey"
              }`}
            >
              {isOnline ? "Online" : "In-person"} Sessions Bundle
            </div>
          </div>

          <div
            className={`tru-bars-wrapper tbk-mx-1 tbk-flex tbk-justify-between tbk-mb-2${
              hasSessions ? "" : " no-sessions"
            }`}
          >
            {bundleLine}
          </div>
          <div className="tbk-text-main tbk-mb-4 tbk-text-grey">
            {sessionsLeft} session{sessionsLeft > 1 ? "s" : ""} left
          </div>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="bundle-pricing-box tbk--mt-3 tbk-flex tbk-flex-wrap tbk-justify-center tbk-text-center">
        <div className="tbk-relative tbk--bottom-3 tbk-inline-flex tbk-min-w-0 tbk-items-center tbk-self-center tbk-rounded-lg tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-bg-basic-white tbk-py-0.5 tbk-px-1.5 tbk-text-primary">
          {isOnline ? (
            <SessionTypeOnlineIcon
              id={iconId}
              className="tbk-mr-0.5 tbk-shrink-0"
            />
          ) : (
            <MapMarkerIcon className="tbk-shrink-0 tbk-text-coral" size={32} />
          )}

          <div className="bundle-pricing-box-title tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
            1-hour {isOnline ? "Online" : "In-Person"} Session with{" "}
            {partnerFirstName}
          </div>
        </div>
        <div className="tbk-flex tbk-min-w-full tbk-grow tbk-items-center tbk-justify-center tbk-rounded-lg tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-pb-4 tbk-pt-5 md:tbk-pr-7 md:tbk-pl-6">
          <div className="tbk-ml-3 tbk-mr-3 tbk-grow tbk-text-left md:tbk-ml-0 md:tbk-mr-4">
            <div className="tbk-text-main tbk-mt-1 tbk-text-primary">
              Single
              <br />
              Session:
            </div>
            <div className="tbk-text-epic-medium tbk-text-primary">
              {price ? price.symbol : "£"}
              {price && price.amount}
            </div>
          </div>

          <ul className="tbk-mb-0 tbk-min-w-0 tbk-grow tbk-list-disc tbk-text-left sm:tbk-pl-3 md:tbk-pl-5">
            {bundles && bundles.length
              ? bundles.map((bundle, index, arr) => (
                  <li
                    className={`${
                      index !== arr.length - 1 ? "tbk-mb-1 " : ""
                    }tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary md:tbk-overflow-initial`}
                    key={index}
                  >
                    {bundle.metadata.sessions} sessions:{" "}
                    {bundle.price.symbol || "£"}
                    {bundle.price.amount}{" "}
                    <span className="tbk-text-green">
                      (-
                      {
                        bundle.price
                          .save /*100 - Math.round(bundle.price / (bundle.price + bundle.discount) * 100)*/
                      }
                      %)
                    </span>
                  </li>
                ))
              : null}
          </ul>
        </div>
      </div>
    );
  }

  return content;
}

BundlePriceBox.propTypes = {
  iconId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  bundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      type: PropTypes.string,
      sessions: PropTypes.number,
      displayName: PropTypes.string,
      price: PropTypes.number,
      save: PropTypes.number,
      discount: PropTypes.number,
      bestSeller: PropTypes.bool,
      description: PropTypes.arrayOf(PropTypes.string),
      labels: PropTypes.string,
      tag: PropTypes.string,
    })
  ),
  customerBundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      sessionsCount: PropTypes.number,
      sessionsUsed: PropTypes.number,
      type: PropTypes.string,
    })
  ),
  sessionType: PropTypes.string,
  partnerId: PropTypes.number,
  partnerFirstName: PropTypes.string,
  price: PropTypes.shape({
    price: PropTypes.number,
    currency: PropTypes.string,
    symbol: PropTypes.string,
  }),
};
